import React, { useCallback, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { coverEditorMessages } from '../../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SwatchesProps {
  /** The name of the colour for a screen reader ("Text colour"). */
  label: string;
  /** The colours of the picture (`doc.palette`). */
  palette: readonly string[];
  value: string;
  disabled?: boolean;
  onChange: (colour: string) => void;
}

const ALWAYS = ['#ffffff', '#111111'];

/**
 * The colour choice of the inspector (design cover-editor b1): the colours of the picture, white
 * and near-black, and "+" for any colour (the colour picker of the browser).
 */
const SwatchesComponent: React.FC<SwatchesProps> = ({ label, palette, value, disabled, onChange }) => {
  const intl = useIntl();
  const customId = useId();
  const colours = useMemo(() => [...new Set([...palette, ...ALWAYS].map((colour) => colour.toLowerCase()))], [palette]);
  const custom = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value.toLowerCase()),
    [onChange]
  );
  const current = value.toLowerCase();
  return (
    <span className={styles.swatches} role="group" aria-label={label}>
      {colours.map((colour) => (
        <button
          key={colour}
          type="button"
          className={clsx(styles.swatch, colour === current && styles.swatchOn)}
          style={{ background: colour }}
          aria-label={colour}
          aria-pressed={colour === current}
          disabled={disabled}
          onClick={() => onChange(colour)}
        />
      ))}
      <label
        htmlFor={customId}
        className={clsx(styles.swatch, styles.swatchAdd, !colours.includes(current) && styles.swatchOn)}
      >
        <span aria-hidden>+</span>
        <input
          id={customId}
          className={styles.swatchInput}
          type="color"
          value={current}
          disabled={disabled}
          aria-label={intl.formatMessage(coverEditorMessages.colourCustom, { name: label })}
          onChange={custom}
        />
      </label>
    </span>
  );
};

export const Swatches = TypedMemo(SwatchesComponent);
